import { Component } from '@theme/component';

/**
 * BONQUERÉ — Announcement bar carousel  (BNQ-WEB-0860 · Sprint 3.4.19 final corrections)
 *
 * A sequential announcement carousel: one message at a time, advancing on a timer and wrapping from
 * the last block back to the first indefinitely. NOT a marquee — nothing scrolls continuously.
 *
 * ── WHAT WAS WRONG WITH THE PREVIOUS VERSION ────────────────────────────────────────────────────
 * Four defects, three of them latent because the live bar only ever had one message:
 *
 *   1. NO `disconnectedCallback`. `setInterval` was started in `connectedCallback` and never
 *      cleared. In the theme editor every section re-render left its timer running, so after a few
 *      edits several timers advanced the same bar at once and it appeared to skip messages.
 *   2. `resume()` COULD NOT RESUME. `#handleVisibilityChange` called `pause()`, which sets the
 *      `paused` attribute; `resume()` opens with `if (!this.autoplay || this.paused) return`. Once
 *      the visitor switched tabs, rotation stopped permanently for the rest of the page's life.
 *   3. `resume()` called `pause()` before `play()` — harmless, but it meant the paused flag and the
 *      interval were managed in two places with different meanings.
 *   4. Fade was the only transition, hard-coded in CSS at 0.5s, with no merchant control over
 *      rotation, style, dwell, pace or hover behaviour.
 *
 * ── DESIGN ──────────────────────────────────────────────────────────────────────────────────────
 * State lives in one place: `#index`. Every slide is given `data-slide-state` of `active`, `exit` or
 * `idle`, and CSS owns the appearance. `idle` slides are parked on the LEFT so the next one always
 * enters from the left and the outgoing one leaves to the right — the approved direction.
 *
 * Slides that are not active are `visibility: hidden` (see the section stylesheet), which is what
 * keeps their links out of the tab order. That is why `visibility` is in the transition list rather
 * than being toggled instantly: it flips only once the fade/slide has finished.
 *
 * NO `aria-live`. The previous markup made the bar a polite live region, so every rotation
 * interrupted screen-reader users with a message they had not asked for. The bar is a labelled
 * region instead; the active message is in the accessibility tree, the others are hidden, and
 * nothing is announced on a timer.
 *
 * Everything is instance-scoped — no globals, no shared timers, no library.
 */
export class AnnouncementBar extends Component {
  /** Index of the visible slide. Wraps with modulo, so it loops forever. @type {number} */
  #index = 0;
  /** @type {ReturnType<typeof setInterval> | undefined} */
  #timer = undefined;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #settle = undefined;
  /** Set while the merchant is editing a block, or the tab is hidden, or a pointer/focus is inside. */
  #held = new Set();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#onHoldStart);
    this.addEventListener('pointerleave', this.#onHoldEnd);
    // `focusin`/`focusout` rather than `focus` — the focusable node is a link inside a slide, and
    // only the bubbling pair reaches this element.
    this.addEventListener('focusin', this.#onFocusHold);
    this.addEventListener('focusout', this.#onFocusRelease);
    document.addEventListener('visibilitychange', this.#onVisibility);

    this.#show(0, false);
    this.#start();
  }

  /**
   * Everything started above is torn down here. This is the fix for duplicate timers in the theme
   * editor: a re-rendered section disconnects its old element, which lands here.
   */
  disconnectedCallback() {
    super.disconnectedCallback?.();

    this.#stop();
    this.removeEventListener('pointerenter', this.#onHoldStart);
    this.removeEventListener('pointerleave', this.#onHoldEnd);
    this.removeEventListener('focusin', this.#onFocusHold);
    this.removeEventListener('focusout', this.#onFocusRelease);
    document.removeEventListener('visibilitychange', this.#onVisibility);
  }

  /** @returns {HTMLElement[]} */
  get slides() {
    return this.refs.slides ?? [];
  }

  /** Dwell per message in ms, or 0 when rotation is off or there is nothing to rotate to. */
  get dwell() {
    const seconds = parseFloat(`${this.getAttribute('autoplay')}`);
    if (Number.isNaN(seconds) || seconds <= 0) return 0;
    return this.slides.length > 1 ? seconds * 1000 : 0;
  }

  /** `slide` | `fade` | `static` */
  get animation() {
    return this.dataset.animation ?? 'slide';
  }

  /** Transition length in ms. `static` and reduced motion resolve to an instant change. */
  get transition() {
    if (this.animation === 'static') return 0;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return 0;
    const ms = parseInt(`${this.dataset.transition}`, 10);
    return Number.isNaN(ms) ? 700 : ms;
  }

  get pauseOnHover() {
    return this.dataset.pauseOnHover !== 'false';
  }

  next() {
    if (this.slides.length < 2) return;
    this.#show(this.#index + 1, true);
  }

  previous() {
    if (this.slides.length < 2) return;
    this.#show(this.#index - 1, true);
  }

  /**
   * Move to a slide. Modulo wrap is what makes the last → first step ordinary rather than a special
   * case, so the loop never stops at the end.
   * @param {number} target
   * @param {boolean} animate
   */
  #show(target, animate) {
    const slides = this.slides;
    if (!slides.length) return;

    const count = slides.length;
    const next = ((target % count) + count) % count;
    const previous = this.#index;
    this.#index = next;

    clearTimeout(this.#settle);

    slides.forEach((slide, index) => {
      let state = 'idle';
      if (index === next) state = 'active';
      else if (animate && index === previous && previous !== next) state = 'exit';

      slide.dataset.slideState = state;
      slide.setAttribute('aria-hidden', `${index !== next}`);
    });

    // Park the outgoing slide back on the left once it has finished leaving, so it is ready to enter
    // again on the next lap. It is already invisible by then, so the reposition is never seen.
    if (animate && previous !== next && this.transition > 0) {
      this.#settle = setTimeout(() => {
        const outgoing = slides[previous];
        if (outgoing && outgoing.dataset.slideState === 'exit') outgoing.dataset.slideState = 'idle';
      }, this.transition + 50);
    }
  }

  #start() {
    this.#stop();
    const dwell = this.dwell;
    if (!dwell || this.#held.size) return;
    this.#timer = setInterval(() => this.next(), dwell);
  }

  #stop() {
    clearInterval(this.#timer);
    this.#timer = undefined;
  }

  /**
   * One hold set for every reason rotation can be suspended — pointer, focus, hidden tab, theme
   * editor. Rotation resumes only when every reason has been released, which is what stops a
   * mouseleave from restarting a bar the merchant is still editing.
   * @param {string} reason
   */
  hold(reason) {
    this.#held.add(reason);
    this.#stop();
    this.toggleAttribute('paused', true);
  }

  /** @param {string} reason */
  release(reason) {
    this.#held.delete(reason);
    if (this.#held.size) return;
    this.toggleAttribute('paused', false);
    this.#start();
  }

  #onHoldStart = () => {
    if (this.pauseOnHover) this.hold('pointer');
  };

  #onHoldEnd = () => this.release('pointer');

  #onFocusHold = () => {
    if (this.pauseOnHover) this.hold('focus');
  };

  #onFocusRelease = () => this.release('focus');

  #onVisibility = () => (document.hidden ? this.hold('hidden') : this.release('hidden'));

  /**
   * Theme editor: show the block the merchant selected and keep it on screen while they edit it.
   * @param {number} index
   */
  selectSlide(index) {
    this.hold('editor');
    this.#show(index, false);
  }

  releaseEditor() {
    this.release('editor');
  }
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}

/**
 * Theme-editor lifecycle. Registered once per document rather than per instance, and every handler
 * resolves the component from the event target, so a re-rendered section is always driven through
 * its current element rather than a stale reference.
 *
 * `shopify:section:load` needs no handler: the re-rendered section brings a new custom element with
 * it, and `connectedCallback` starts it. The old one's `disconnectedCallback` has already stopped
 * its timer, which is what prevents the duplicate-timer defect.
 */
if (window.Shopify?.designMode) {
  const bar = (event) => event.target?.querySelector?.('announcement-bar-component');

  document.addEventListener('shopify:block:select', (event) => {
    const component = bar(event) ?? event.target?.closest?.('announcement-bar-component');
    if (!(component instanceof AnnouncementBar)) return;
    const index = component.slides.indexOf(/** @type {HTMLElement} */ (event.target));
    component.selectSlide(index > -1 ? index : 0);
  });

  document.addEventListener('shopify:block:deselect', (event) => {
    const component = bar(event) ?? event.target?.closest?.('announcement-bar-component');
    if (component instanceof AnnouncementBar) component.releaseEditor();
  });

  document.addEventListener('shopify:section:select', (event) => {
    const component = bar(event);
    if (component instanceof AnnouncementBar) component.hold('editor');
  });

  document.addEventListener('shopify:section:deselect', (event) => {
    const component = bar(event);
    if (component instanceof AnnouncementBar) component.releaseEditor();
  });
}
